/* eslint-disable */
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import {
  Box,
  TextField,
  Button,
  IconButton,
  Stack,
  MenuItem,
  Typography,
  CircularProgress,
  useTheme,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
} from "@mui/material";
import {
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  AttachMoney as MoneyIcon,
  Calculate as CalculateIcon,
  Percent as PercentIcon,
  Business as BusinessIcon,
  Flag as FlagIcon,
  Receipt as TaxIcon,
} from "@mui/icons-material";
import { createNewSalaryStructure } from "store/features/company/payrollManagement/salaryStructure/salaryStructureActions";
import { fetchDepartments } from "store/features/company/department/departmentActions";
import { selectDepartments } from "store/features/company/department/departmentSelectors";
import addSalaryStructureSvg from "assets/modals/add.svg";

const AddSalaryStructureModal = ({ isOpen, onClose, onSuccess }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const departments = useSelector(selectDepartments);

  useEffect(() => {
    if (isOpen) {
      dispatch(fetchDepartments());
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isOpen, dispatch]);

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    department_id: "",
    level: "",
    grade: "",
    basic_salary_min: "",
    basic_salary_max: "",
    basic_salary_default: "",
    hra_type: "percentage",
    hra_value: "",
    conveyance_amount: "",
    education_allowance_amount: "",
    transport_allowance_amount: "",
    fixed_allowance_type: "percentage",
    fixed_allowance_value: "",
    gratuity_percentage: "",
    commission_percentage: "",
    fuel_reimbursement_max: "",
    telephone_reimbursement_max: "",
    driver_reimbursement_max: "",
    vehicle_maintenance_reimbursement_max: "",
    lta_reimbursement_max: "",
    provident_fund_percentage: "",
    professional_tax_slab: {
      slabs: [{ min: 0, max: 999999, amount: 0 }],
    },
    tax_deduction_percentage: "",
    bonus_percentage: "",
    overtime_rate: "",
    status: "active",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const formattedData = {
      ...formData,
      department_id: Number(formData.department_id),
      basic_salary_min: parseFloat(formData.basic_salary_min),
      basic_salary_max: parseFloat(formData.basic_salary_max),
      basic_salary_default: parseFloat(formData.basic_salary_default),
      hra_value: parseFloat(formData.hra_value),
      conveyance_amount: parseFloat(formData.conveyance_amount) || 0,
      education_allowance_amount:
        parseFloat(formData.education_allowance_amount) || 0,
      transport_allowance_amount:
        parseFloat(formData.transport_allowance_amount) || 0,
      fixed_allowance_value: parseFloat(formData.fixed_allowance_value) || 0,
      gratuity_percentage: parseFloat(formData.gratuity_percentage) || 0,
      commission_percentage: parseFloat(formData.commission_percentage) || 0,
      fuel_reimbursement_max: parseFloat(formData.fuel_reimbursement_max) || 0,
      telephone_reimbursement_max:
        parseFloat(formData.telephone_reimbursement_max) || 0,
      driver_reimbursement_max:
        parseFloat(formData.driver_reimbursement_max) || 0,
      vehicle_maintenance_reimbursement_max:
        parseFloat(formData.vehicle_maintenance_reimbursement_max) || 0,
      lta_reimbursement_max: parseFloat(formData.lta_reimbursement_max) || 0,
      provident_fund_percentage: parseFloat(formData.provident_fund_percentage),
      tax_deduction_percentage:
        parseFloat(formData.tax_deduction_percentage) || 0,
      bonus_percentage: parseFloat(formData.bonus_percentage) || 0,
      overtime_rate: parseFloat(formData.overtime_rate) || 0,
    };

    const result = await dispatch(createNewSalaryStructure(formattedData));

    if (result.success) {
      onSuccess();
      onClose();
      resetForm();
    }
    setLoading(false);
  };

  const resetForm = () => {
    setFormData({
      department_id: "",
      level: "",
      grade: "",
      basic_salary_min: "",
      basic_salary_max: "",
      basic_salary_default: "",
      hra_type: "percentage",
      hra_value: "",
      conveyance_amount: "",
      education_allowance_amount: "",
      transport_allowance_amount: "",
      fixed_allowance_type: "percentage",
      fixed_allowance_value: "",
      gratuity_percentage: "",
      commission_percentage: "",
      fuel_reimbursement_max: "",
      telephone_reimbursement_max: "",
      driver_reimbursement_max: "",
      vehicle_maintenance_reimbursement_max: "",
      lta_reimbursement_max: "",
      provident_fund_percentage: "",
      professional_tax_slab: {
        slabs: [{ min: 0, max: 999999, amount: 0 }],
      },
      tax_deduction_percentage: "",
      bonus_percentage: "",
      overtime_rate: "",
      status: "active",
    });
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      backgroundColor: theme.palette.mode === "dark" ? "#1e293b" : "#f9fafb",
      "& fieldset": {
        borderColor: theme.palette.mode === "dark" ? "#475569" : "#d1d5db",
      },
      "&:hover fieldset": {
        borderColor: "#f59e0b",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#f59e0b",
        borderWidth: "2px",
      },
    },
    "& .MuiInputLabel-root": {
      color: theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
      position: "relative",
      transform: "none",
      marginBottom: "8px",
      display: "block",
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: "#f59e0b",
    },
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <style>
            {`
        .MuiPopover-root {
          z-index: 1400 !important;
        }
        .MuiPopper-root {
          z-index: 1400 !important;
        }
        .MuiMenu-root {
          z-index: 1400 !important;
        }
      `}
          </style>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 1300,
              backgroundColor: "rgba(0, 0, 0, 0.6)",
              backdropFilter: "blur(4px)",
            }}
          />

          <motion.div
            initial={{ x: "100%", opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: "100%", opacity: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            style={{
              position: "fixed",
              right: 0,
              top: 0,
              zIndex: 1301,
              height: "100%",
              width: "100%",
              maxWidth: "800px",
              overflowY: "auto",
              backgroundColor:
                theme.palette.mode === "dark" ? "#0f172a" : "#ffffff",
              boxShadow: theme.shadows[24],
            }}
          >
            {/* Header */}
            <Box
              sx={{
                position: "sticky",
                top: 0,
                zIndex: 10,
                borderBottom: `1px solid ${
                  theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6"
                }`,
                backgroundColor:
                  theme.palette.mode === "dark" ? "#0f172a" : "#ffffff",
              }}
            >
              <Box sx={{ p: 3, pr: 7 }}>
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <Box flexShrink={0}>
                    <Box
                      component="img"
                      src={addSalaryStructureSvg}
                      alt="Add Salary Structure"
                      sx={{ width: 80, height: 80, objectFit: "contain" }}
                    />
                  </Box>
                  <Box flex={1}>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: "bold",
                        color:
                          theme.palette.mode === "dark" ? "#ffffff" : "#111827",
                      }}
                    >
                      Add Salary Structure
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        mt: 0.5,
                        color:
                          theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                      }}
                    >
                      Create a new salary structure for a department
                    </Typography>
                  </Box>
                </Stack>
              </Box>
              <IconButton
                onClick={onClose}
                sx={{
                  position: "absolute",
                  top: 20,
                  right: 20,
                  width: 36,
                  height: 36,
                  borderRadius: "8px",
                  "&:hover": { backgroundColor: "#f59e0b", color: "#ffffff" },
                }}
              >
                <CloseIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Box>

            {/* Form */}
            <form onSubmit={handleSubmit}>
              <Box sx={{ p: 3 }}>
                <Stack spacing={3}>
                  {/* Basic Information Section */}
                  <Accordion
                    defaultExpanded
                    sx={{
                      borderRadius: "16px !important",
                      backgroundColor:
                        theme.palette.mode === "dark" ? "#1e293b" : "#f9fafb",
                      boxShadow: "none",
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        "& .MuiAccordionSummary-content": {
                          alignItems: "center",
                          gap: 1,
                        },
                      }}
                    >
                      <BusinessIcon sx={{ color: "#f59e0b" }} />
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Basic Information
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Stack spacing={2}>
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={2}
                        >
                          <TextField
                            fullWidth
                            select
                            label="Department"
                            name="department_id"
                            value={formData.department_id}
                            onChange={handleChange}
                            required
                            sx={inputSx}
                            InputLabelProps={{ shrink: true }}
                            SelectProps={{
                              MenuProps: {
                                sx: { zIndex: 1400 },
                                PaperProps: {
                                  sx: { zIndex: 1400 },
                                },
                                disableScrollLock: true,
                              },
                            }}
                          >
                            {departments.map((dept) => (
                              <MenuItem key={dept.id} value={dept.id}>
                                {dept.name} ({dept.code})
                              </MenuItem>
                            ))}
                          </TextField>
                          <TextField
                            fullWidth
                            label="Level"
                            name="level"
                            value={formData.level}
                            onChange={handleChange}
                            required
                            placeholder="e.g., Junior, Senior, Lead"
                            sx={inputSx}
                            InputLabelProps={{ shrink: true }}
                          />
                          <TextField
                            fullWidth
                            label="Grade"
                            name="grade"
                            value={formData.grade}
                            onChange={handleChange}
                            required
                            placeholder="e.g., A, B, C, D"
                            sx={inputSx}
                            InputLabelProps={{ shrink: true }}
                          />
                        </Stack>
                      </Stack>
                    </AccordionDetails>
                  </Accordion>

                  {/* Salary Range Section */}
                  <Accordion
                    defaultExpanded
                    sx={{
                      borderRadius: "16px !important",
                      backgroundColor:
                        theme.palette.mode === "dark" ? "#1e293b" : "#f9fafb",
                      boxShadow: "none",
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        "& .MuiAccordionSummary-content": {
                          alignItems: "center",
                          gap: 1,
                        },
                      }}
                    >
                      <MoneyIcon sx={{ color: "#f59e0b" }} />
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Salary Range
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Stack spacing={2}>
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={2}
                        >
                          <TextField
                            fullWidth
                            label="Basic Salary Min (₹)"
                            name="basic_salary_min"
                            type="number"
                            value={formData.basic_salary_min}
                            onChange={handleChange}
                            required
                            sx={inputSx}
                            InputLabelProps={{ shrink: true }}
                          />
                          <TextField
                            fullWidth
                            label="Basic Salary Max (₹)"
                            name="basic_salary_max"
                            type="number"
                            value={formData.basic_salary_max}
                            onChange={handleChange}
                            required
                            sx={inputSx}
                            InputLabelProps={{ shrink: true }}
                          />
                          <TextField
                            fullWidth
                            label="Basic Salary Default (₹)"
                            name="basic_salary_default"
                            type="number"
                            value={formData.basic_salary_default}
                            onChange={handleChange}
                            required
                            sx={inputSx}
                            InputLabelProps={{ shrink: true }}
                          />
                        </Stack>
                      </Stack>
                    </AccordionDetails>
                  </Accordion>

                  {/* Allowances Section */}
                  <Accordion
                    sx={{
                      borderRadius: "16px !important",
                      backgroundColor:
                        theme.palette.mode === "dark" ? "#1e293b" : "#f9fafb",
                      boxShadow: "none",
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        "& .MuiAccordionSummary-content": {
                          alignItems: "center",
                          gap: 1,
                        },
                      }}
                    >
                      <PercentIcon sx={{ color: "#f59e0b" }} />
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Allowances & Benefits
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Stack spacing={2}>
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={2}
                        >
                          <TextField
                            fullWidth
                            select
                            label="HRA Type"
                            name="hra_type"
                            value={formData.hra_type}
                            onChange={handleChange}
                            required
                            sx={inputSx}
                          >
                            <MenuItem value="percentage">
                              Percentage of Basic
                            </MenuItem>
                            <MenuItem value="fixed">Fixed Amount</MenuItem>
                          </TextField>
                          <TextField
                            fullWidth
                            label={`HRA Value (${
                              formData.hra_type === "percentage" ? "%" : "₹"
                            })`}
                            name="hra_value"
                            type="number"
                            value={formData.hra_value}
                            onChange={handleChange}
                            required
                            sx={inputSx}
                            InputLabelProps={{ shrink: true }}
                          />
                        </Stack>
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={2}
                        >
                          <TextField
                            fullWidth
                            label="Conveyance Allowance (₹)"
                            name="conveyance_amount"
                            type="number"
                            value={formData.conveyance_amount}
                            onChange={handleChange}
                            sx={inputSx}
                            InputLabelProps={{ shrink: true }}
                          />
                          <TextField
                            fullWidth
                            label="Education Allowance (₹)"
                            name="education_allowance_amount"
                            type="number"
                            value={formData.education_allowance_amount}
                            onChange={handleChange}
                            sx={inputSx}
                            InputLabelProps={{ shrink: true }}
                          />
                          <TextField
                            fullWidth
                            label="Transport Allowance (₹)"
                            name="transport_allowance_amount"
                            type="number"
                            value={formData.transport_allowance_amount}
                            onChange={handleChange}
                            sx={inputSx}
                            InputLabelProps={{ shrink: true }}
                          />
                        </Stack>
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={2}
                        >
                          <TextField
                            fullWidth
                            select
                            label="Fixed Allowance Type"
                            name="fixed_allowance_type"
                            value={formData.fixed_allowance_type}
                            onChange={handleChange}
                            sx={inputSx}
                          >
                            <MenuItem value="percentage">
                              Percentage of Basic
                            </MenuItem>
                            <MenuItem value="fixed">Fixed Amount</MenuItem>
                          </TextField>
                          <TextField
                            fullWidth
                            label={`Fixed Allowance Value (${
                              formData.fixed_allowance_type === "percentage"
                                ? "%"
                                : "₹"
                            })`}
                            name="fixed_allowance_value"
                            type="number"
                            value={formData.fixed_allowance_value}
                            onChange={handleChange}
                            sx={inputSx}
                            InputLabelProps={{ shrink: true }}
                          />
                        </Stack>
                      </Stack>
                    </AccordionDetails>
                  </Accordion>

                  {/* Deductions Section */}
                  <Accordion
                    sx={{
                      borderRadius: "16px !important",
                      backgroundColor:
                        theme.palette.mode === "dark" ? "#1e293b" : "#f9fafb",
                      boxShadow: "none",
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        "& .MuiAccordionSummary-content": {
                          alignItems: "center",
                          gap: 1,
                        },
                      }}
                    >
                      <CalculateIcon sx={{ color: "#f59e0b" }} />
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Deductions & Contributions
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Stack spacing={2}>
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={2}
                        >
                          <TextField
                            fullWidth
                            label="Provident Fund (%)"
                            name="provident_fund_percentage"
                            type="number"
                            value={formData.provident_fund_percentage}
                            onChange={handleChange}
                            required
                            sx={inputSx}
                            InputLabelProps={{ shrink: true }}
                          />
                          <TextField
                            fullWidth
                            label="Gratuity (%)"
                            name="gratuity_percentage"
                            type="number"
                            value={formData.gratuity_percentage}
                            onChange={handleChange}
                            sx={inputSx}
                            InputLabelProps={{ shrink: true }}
                          />
                          <TextField
                            fullWidth
                            label="Commission (%)"
                            name="commission_percentage"
                            type="number"
                            value={formData.commission_percentage}
                            onChange={handleChange}
                            sx={inputSx}
                            InputLabelProps={{ shrink: true }}
                          />
                        </Stack>
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={2}
                        >
                          <TextField
                            fullWidth
                            label="Tax Deduction (%)"
                            name="tax_deduction_percentage"
                            type="number"
                            value={formData.tax_deduction_percentage}
                            onChange={handleChange}
                            sx={inputSx}
                            InputLabelProps={{ shrink: true }}
                          />
                          <TextField
                            fullWidth
                            label="Bonus (%)"
                            name="bonus_percentage"
                            type="number"
                            value={formData.bonus_percentage}
                            onChange={handleChange}
                            sx={inputSx}
                            InputLabelProps={{ shrink: true }}
                          />
                          <TextField
                            fullWidth
                            label="Overtime Rate (₹/hr)"
                            name="overtime_rate"
                            type="number"
                            value={formData.overtime_rate}
                            onChange={handleChange}
                            sx={inputSx}
                            InputLabelProps={{ shrink: true }}
                          />
                        </Stack>
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                  {/* Professional Tax Slabs Section */}
                  <Accordion
                    sx={{
                      borderRadius: "16px !important",
                      backgroundColor:
                        theme.palette.mode === "dark" ? "#1e293b" : "#f9fafb",
                      boxShadow: "none",
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        "& .MuiAccordionSummary-content": {
                          alignItems: "center",
                          gap: 1,
                        },
                      }}
                    >
                      <TaxIcon sx={{ color: "#f59e0b" }} />
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Professional Tax Slabs
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Stack spacing={2}>
                        {formData.professional_tax_slab.slabs.map(
                          (slab, index) => (
                            <Stack key={index} spacing={2}>
                              <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 600 }}
                              >
                                Slab {index + 1}
                              </Typography>
                              <Stack
                                direction={{ xs: "column", sm: "row" }}
                                spacing={2}
                              >
                                <TextField
                                  fullWidth
                                  label="Min Salary (₹)"
                                  type="number"
                                  value={slab.min}
                                  onChange={(e) => {
                                    const newSlabs = [
                                      ...formData.professional_tax_slab.slabs,
                                    ];
                                    newSlabs[index].min = parseFloat(
                                      e.target.value,
                                    );
                                    setFormData({
                                      ...formData,
                                      professional_tax_slab: {
                                        slabs: newSlabs,
                                      },
                                    });
                                  }}
                                  required
                                  sx={inputSx}
                                  InputLabelProps={{ shrink: true }}
                                />
                                <TextField
                                  fullWidth
                                  label="Max Salary (₹)"
                                  type="number"
                                  value={slab.max}
                                  onChange={(e) => {
                                    const newSlabs = [
                                      ...formData.professional_tax_slab.slabs,
                                    ];
                                    newSlabs[index].max = parseFloat(
                                      e.target.value,
                                    );
                                    setFormData({
                                      ...formData,
                                      professional_tax_slab: {
                                        slabs: newSlabs,
                                      },
                                    });
                                  }}
                                  required
                                  sx={inputSx}
                                  InputLabelProps={{ shrink: true }}
                                  helperText={
                                    slab.max === 999999
                                      ? "Use 999999 for 'Above' category"
                                      : ""
                                  }
                                />
                                <TextField
                                  fullWidth
                                  label="Tax Amount (₹)"
                                  type="number"
                                  value={slab.amount}
                                  onChange={(e) => {
                                    const newSlabs = [
                                      ...formData.professional_tax_slab.slabs,
                                    ];
                                    newSlabs[index].amount = parseFloat(
                                      e.target.value,
                                    );
                                    setFormData({
                                      ...formData,
                                      professional_tax_slab: {
                                        slabs: newSlabs,
                                      },
                                    });
                                  }}
                                  required
                                  sx={inputSx}
                                  InputLabelProps={{ shrink: true }}
                                />
                              </Stack>
                              {formData.professional_tax_slab.slabs.length >
                                1 && (
                                <Button
                                  onClick={() => {
                                    const newSlabs =
                                      formData.professional_tax_slab.slabs.filter(
                                        (_, i) => i !== index,
                                      );
                                    setFormData({
                                      ...formData,
                                      professional_tax_slab: {
                                        slabs: newSlabs,
                                      },
                                    });
                                  }}
                                  sx={{
                                    alignSelf: "flex-start",
                                    color: "#ef4444",
                                    textTransform: "none",
                                  }}
                                >
                                  Remove Slab
                                </Button>
                              )}
                              {index ===
                                formData.professional_tax_slab.slabs.length -
                                  1 && (
                                <Button
                                  onClick={() => {
                                    setFormData({
                                      ...formData,
                                      professional_tax_slab: {
                                        slabs: [
                                          ...formData.professional_tax_slab
                                            .slabs,
                                          { min: 0, max: 0, amount: 0 },
                                        ],
                                      },
                                    });
                                  }}
                                  sx={{
                                    alignSelf: "flex-start",
                                    color: "#f59e0b",
                                    textTransform: "none",
                                    bgcolor:
                                      theme.palette.mode === "dark"
                                        ? "rgba(245, 158, 11, 0.1)"
                                        : "rgba(245, 158, 11, 0.05)",
                                    border: `1px solid ${
                                      theme.palette.mode === "dark"
                                        ? "rgba(245, 158, 11, 0.3)"
                                        : "rgba(245, 158, 11, 0.2)"
                                    }`,
                                    borderRadius: "8px",
                                    px: 2,
                                    py: 0.5,
                                    "&:hover": {
                                      bgcolor: "rgba(245, 158, 11, 0.15)",
                                      border: `1px solid #f59e0b`,
                                    },
                                  }}
                                >
                                  + Add Another Slab
                                </Button>
                              )}
                            </Stack>
                          ),
                        )}
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                  {/* Status Section */}
                  <Accordion
                    sx={{
                      borderRadius: "16px !important",
                      backgroundColor:
                        theme.palette.mode === "dark" ? "#1e293b" : "#f9fafb",
                      boxShadow: "none",
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        "& .MuiAccordionSummary-content": {
                          alignItems: "center",
                          gap: 1,
                        },
                      }}
                    >
                      <FlagIcon sx={{ color: "#f59e0b" }} />{" "}
                      {/* Changed from ChipIcon */}
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Status Configuration
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Stack spacing={2}>
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={2}
                        >
                          <TextField
                            fullWidth
                            select
                            label="Status"
                            name="status"
                            value={formData.status}
                            onChange={handleChange}
                            required
                            sx={inputSx}
                            InputLabelProps={{ shrink: true }}
                          >
                            <MenuItem value="active">Active</MenuItem>
                            <MenuItem value="inactive">Inactive</MenuItem>
                          </TextField>
                        </Stack>
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                  {/* Reimbursements Section */}
                  <Accordion
                    sx={{
                      borderRadius: "16px !important",
                      backgroundColor:
                        theme.palette.mode === "dark" ? "#1e293b" : "#f9fafb",
                      boxShadow: "none",
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        "& .MuiAccordionSummary-content": {
                          alignItems: "center",
                          gap: 1,
                        },
                      }}
                    >
                      <MoneyIcon sx={{ color: "#f59e0b" }} />
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Reimbursements (Max Limits)
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Stack spacing={2}>
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={2}
                        >
                          <TextField
                            fullWidth
                            label="Fuel Reimbursement (₹)"
                            name="fuel_reimbursement_max"
                            type="number"
                            value={formData.fuel_reimbursement_max}
                            onChange={handleChange}
                            sx={inputSx}
                            InputLabelProps={{ shrink: true }}
                          />
                          <TextField
                            fullWidth
                            label="Telephone Reimbursement (₹)"
                            name="telephone_reimbursement_max"
                            type="number"
                            value={formData.telephone_reimbursement_max}
                            onChange={handleChange}
                            sx={inputSx}
                            InputLabelProps={{ shrink: true }}
                          />
                        </Stack>
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={2}
                        >
                          <TextField
                            fullWidth
                            label="Driver Reimbursement(₹)"
                            name="driver_reimbursement_max"
                            type="number"
                            value={formData.driver_reimbursement_max}
                            onChange={handleChange}
                            sx={inputSx}
                            InputLabelProps={{ shrink: true }}
                          />
                          <TextField
                            fullWidth
                            label="Vehicle Maintenance (₹)"
                            name="vehicle_maintenance_reimbursement_max"
                            type="number"
                            value={
                              formData.vehicle_maintenance_reimbursement_max
                            }
                            onChange={handleChange}
                            sx={inputSx}
                            InputLabelProps={{ shrink: true }}
                          />
                          <TextField
                            fullWidth
                            label="LTA Reimbursement (₹)"
                            name="lta_reimbursement_max"
                            type="number"
                            value={formData.lta_reimbursement_max}
                            onChange={handleChange}
                            sx={inputSx}
                            InputLabelProps={{ shrink: true }}
                          />
                        </Stack>
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                </Stack>
              </Box>

              {/* Footer Buttons */}
              <Box
                sx={{
                  p: 3,
                  borderTop: `1px solid ${
                    theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6"
                  }`,
                  display: "flex",
                  gap: 2,
                }}
              >
                <Button
                  onClick={onClose}
                  disabled={loading}
                  fullWidth
                  variant="outlined"
                  sx={{
                    borderRadius: "12px",
                    py: 1.2,
                    textTransform: "none",
                    fontWeight: 500,
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  fullWidth
                  variant="contained"
                  sx={{
                    borderRadius: "12px",
                    py: 1.2,
                    textTransform: "none",
                    fontWeight: 500,
                    background:
                      "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                  }}
                >
                  {loading ? (
                    <CircularProgress size={24} sx={{ color: "#ffffff" }} />
                  ) : (
                    "Create Salary Structure"
                  )}
                </Button>
              </Box>
            </form>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default AddSalaryStructureModal;
